"use client"

/**
 * Site-wide motion primitives. Wrap motion v12 with the shared presets in
 * `lib/motion-presets.ts` so callers don't have to repeat the `whileInView` /
 * `viewport` / `transition` boilerplate, and so every reveal automatically
 * short-circuits when `prefers-reduced-motion: reduce` is set.
 */

import * as React from "react"
import {
  motion,
  useInView,
  useMotionValue,
  animate,
  type Variants,
  type Transition,
  type HTMLMotionProps,
} from "motion/react"
import {
  FADE_TRANSITION,
  FADE_TRANSITION_SOFT,
  HOVER_SPRING,
  STAGGER_CONTAINER,
  STAGGER_ITEM,
  EASE_OUT_QUINT,
} from "@/lib/motion-presets"
import { cn } from "@/lib/utils"

type RevealTag = "div" | "section" | "article" | "header" | "main" | "ul" | "li" | "figure"

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

function getReducedMotionSnapshot() {
  return typeof window !== "undefined" && window.matchMedia?.(REDUCED_MOTION_QUERY).matches
}

function subscribeReducedMotion(onStoreChange: () => void) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {}

  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY)
  mediaQuery.addEventListener("change", onStoreChange)
  return () => mediaQuery.removeEventListener("change", onStoreChange)
}

function usePrefersReducedMotion() {
  return React.useSyncExternalStore(subscribeReducedMotion, getReducedMotionSnapshot, () => false)
}

interface RevealProps extends Omit<HTMLMotionProps<"div">, "initial" | "animate" | "whileInView" | "viewport"> {
  /** Vertical travel in px before settling. Defaults to 12. */
  y?: number
  /** Delay in seconds before the reveal kicks off. */
  delay?: number
  /** Use the longer hero-scale duration (420ms ease-out-expo). */
  soft?: boolean
  /** Use mount animation instead of scroll-into-view. */
  onMount?: boolean
  /** Negative bottom margin pushes the trigger below the viewport edge. */
  rootMargin?: string
  /** Underlying HTML element. Defaults to "div". */
  as?: RevealTag
  /** Optional id — useful when wrapping a `<section>` with an anchor target. */
  id?: string
}

/**
 * Fade + translate reveal. By default fires when the element scrolls into the
 * viewport (once). Set `onMount` to fire on first paint instead — useful for
 * above-the-fold heroes.
 */
export function Reveal({
  y = 12,
  delay = 0,
  soft = false,
  onMount = false,
  rootMargin = "0px 0px 10% 0px",
  as = "div",
  id,
  className,
  children,
  ...rest
}: RevealProps) {
  const reduce = usePrefersReducedMotion()
  const transition: Transition = {
    ...(soft ? FADE_TRANSITION_SOFT : FADE_TRANSITION),
    delay,
  }

  if (reduce) {
    return React.createElement(
      as,
      { ...(rest as React.HTMLAttributes<HTMLElement>), className, id },
      children as React.ReactNode
    )
  }

  // motion exposes typed wrappers as `motion.div`, `motion.section`, etc.
  const MotionTag = motion[as] as typeof motion.div

  if (onMount) {
    return (
      <MotionTag
        id={id}
        initial={{ opacity: 0, y }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition}
        className={className}
        {...rest}
      >
        {children}
      </MotionTag>
    )
  }

  return (
    <MotionTag
      id={id}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05, margin: rootMargin }}
      transition={transition}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  )
}

interface StaggerProps extends Omit<HTMLMotionProps<"div">, "variants" | "initial" | "animate" | "whileInView" | "viewport"> {
  onMount?: boolean
  rootMargin?: string
  /** Override stagger gap in seconds. */
  staggerChildren?: number
  /** Override delayChildren in seconds. */
  delayChildren?: number
}

/**
 * Container that cascades children into view with the shared stagger variants.
 * Children should use `<Stagger.Item>` (or any motion element with
 * `variants={STAGGER_ITEM}`).
 */
function StaggerRoot({
  onMount = false,
  rootMargin = "0px 0px 10% 0px",
  staggerChildren,
  delayChildren,
  className,
  children,
  ...rest
}: StaggerProps) {
  const reduce = usePrefersReducedMotion()

  if (reduce) {
    return (
      <div className={className} {...(rest as React.HTMLAttributes<HTMLDivElement>)}>
        {children as React.ReactNode}
      </div>
    )
  }

  const variants: Variants =
    staggerChildren !== undefined || delayChildren !== undefined
      ? {
          hidden: {},
          show: {
            transition: {
              staggerChildren: staggerChildren ?? 0.06,
              delayChildren: delayChildren ?? 0.04,
            },
          },
        }
      : (STAGGER_CONTAINER as Variants)

  const triggerProps = onMount
    ? { initial: "hidden" as const, animate: "show" as const }
    : {
        initial: "hidden" as const,
        whileInView: "show" as const,
        viewport: { once: true, amount: 0.05, margin: rootMargin },
      }

  return (
    <motion.div variants={variants} {...triggerProps} className={className} {...rest}>
      {children}
    </motion.div>
  )
}

interface StaggerItemProps extends Omit<HTMLMotionProps<"div">, "variants" | "initial" | "animate" | "whileInView" | "viewport"> {}

export function StaggerItem({ className, children, ...rest }: StaggerItemProps) {
  const reduce = usePrefersReducedMotion()
  if (reduce) {
    return (
      <div className={className} {...(rest as React.HTMLAttributes<HTMLDivElement>)}>
        {children as React.ReactNode}
      </div>
    )
  }
  return (
    <motion.div variants={STAGGER_ITEM as Variants} className={className} {...rest}>
      {children}
    </motion.div>
  )
}

export const Stagger = Object.assign(StaggerRoot, { Item: StaggerItem })

interface HoverLiftProps extends Omit<HTMLMotionProps<"div">, "whileHover" | "whileTap" | "transition" | "initial" | "animate" | "whileInView" | "viewport" | "variants"> {
  /** Lift distance in px. Defaults to -2. */
  lift?: number
  /** Press scale on tap. Defaults to 0.99. Set to 1 to disable. */
  pressScale?: number
}

/**
 * Tasteful card/link hover lift. Uses a critically-damped spring so the rise
 * feels crisp without wobble. No-op under reduced motion.
 */
export function HoverLift({
  lift = -2,
  pressScale = 0.99,
  className,
  children,
  ...rest
}: HoverLiftProps) {
  const reduce = usePrefersReducedMotion()
  if (reduce) {
    return (
      <div className={className} {...(rest as React.HTMLAttributes<HTMLDivElement>)}>
        {children as React.ReactNode}
      </div>
    )
  }
  return (
    <motion.div
      whileHover={{ y: lift }}
      whileTap={pressScale === 1 ? undefined : { scale: pressScale }}
      transition={HOVER_SPRING as Transition}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  )
}

interface CountUpProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: number
  /** Duration in seconds. Defaults to 1.0. */
  duration?: number
  /** Decimal places. Defaults to 0. */
  decimals?: number
  /** Optional formatter, e.g. for thousand separators. */
  format?: (value: number) => string
  /** Trigger on mount instead of scroll-into-view. */
  onMount?: boolean
}

/**
 * Number ticker. Animates from 0 → `value` once when scrolled into view.
 * Falls back to the final value immediately under reduced motion.
 */
export function CountUp({
  value,
  duration = 1,
  decimals = 0,
  format,
  onMount = false,
  ...rest
}: CountUpProps) {
  const reduce = usePrefersReducedMotion()
  const ref = React.useRef<HTMLSpanElement | null>(null)
  const inView = useInView(ref, { once: true, amount: 0.05, margin: "0px 0px 10% 0px" })
  const mv = useMotionValue(0)
  const [display, setDisplay] = React.useState(() =>
    reduce ? format?.(value) ?? value.toFixed(decimals) : format?.(0) ?? (0).toFixed(decimals)
  )

  React.useEffect(() => {
    if (reduce) {
      setDisplay(format?.(value) ?? value.toFixed(decimals))
      return
    }
    if (!onMount && !inView) return
    const controls = animate(mv, value, {
      duration,
      ease: EASE_OUT_QUINT,
      onUpdate: (latest) => {
        setDisplay(format?.(latest) ?? latest.toFixed(decimals))
      },
    })
    return () => controls.stop()
  }, [reduce, inView, onMount, value, duration, decimals, format, mv])

  return (
    <span ref={ref} {...rest}>
      {display}
    </span>
  )
}

/** Helper for callers that want the cn-merged className without losing children typing. */
export const motionCn = cn
